import { useEffect } from "react";
import tippy from "tippy.js";
import useMatchupStore from "../store/MatchupStore";
import { MatchupAPI } from "./matchupApi";

export function initStaticData(staticData) {
  const matchupStore = useMatchupStore.getState();

  matchupStore.setFixtureId(staticData.fixtureId);
  matchupStore.setLeagueSeasonData(staticData.leagueSeason);
  matchupStore.setLeagueSeasonId(staticData.leagueSeason.id);
  matchupStore.setPlayerStats(staticData.playerStats);
  matchupStore.setSportFixtures(staticData.sportFixtures);
  matchupStore.setTeam1(staticData.team1);
  matchupStore.setTeam2(staticData.team2);
  matchupStore.setFormula(staticData.formula);
  matchupStore.setActualTeam1Stats(staticData.team1.stats);
  matchupStore.setActualTeam2Stats(staticData.team2.stats);

  calculateAndStoreProjPlayerStats(staticData);
  calculateAndStoreProjTeamStats(staticData);
}

export const updateMatchupStore = (staticData) => {
  const matchupStore = useMatchupStore.getState();

  matchupStore.setFixtureId(staticData.fixtureId);
  matchupStore.setLeagueSeasonData(staticData.leagueSeason);
  matchupStore.setLeagueSeasonId(staticData.leagueSeason.id);
  matchupStore.setPlayerStats(staticData.playerStats);

  matchupStore.setLastRefreshed(new Date());
  matchupStore.setSportFixtures(staticData.sportFixtures);
  matchupStore.setTeam1(staticData.team1);
  matchupStore.setTeam2(staticData.team2);

  matchupStore.setActualTeam1Stats(staticData.team1.stats);
  matchupStore.setActualTeam2Stats(staticData.team2.stats);

  calculateAndStoreProjPlayerStats(staticData);
  calculateAndStoreProjTeamStats(staticData);

  updateScoreboardHeader();
};

export function updateScoreboardHeader() {
  const matchupStore = useMatchupStore.getState();

  if (matchupStore.showProjStats) {
    setTeamScoresToProjected();
  } else {
    setTeamScoresToActual();
  }
}

const randomDelay = (min, max) => {
  const duration = Math.floor(Math.random() * (max - min + 1)) + min;
  return new Promise((resolve) => setTimeout(resolve, duration));
};

export const refreshMatchupData = async () => {
  const matchupStore = useMatchupStore.getState();
  const fixtureId = matchupStore.fixtureId;

  matchupStore.setIsRefreshing(true);

  const delay = randomDelay(500, 1000);

  try {
    const [staticData] = await Promise.all([
      MatchupAPI.fetchMatchupData(fixtureId),
      delay,
    ]);

    updateMatchupStore(staticData);
  } catch (error) {
    console.error("Error refreshing player stats:", error);
  } finally {
    matchupStore.setIsRefreshing(false);
  }
};

export const winningStyles =
  "bg-slate-100 font-semibold dark:bg-gray-800 dark:text-gray-200";
export const losingStyles = "text-gray-700 dark:text-gray-400";

export function isTeamWinning(fixtureTeam, opponent, weights, stat) {
  const value = weights[stat];

  if (value === 0) {
    return false;
  } else if (value > 0) {
    return fixtureTeam.stats[stat] > opponent.stats[stat];
  } else {
    return fixtureTeam.stats[stat] < opponent.stats[stat];
  }
}

export function renderStat(rawStats, statToRender) {
  if (!rawStats) return "-";

  if (statToRender === "score") {
    return `${rawStats.goals}-${rawStats.behinds} (${rawStats.goals * 6 + rawStats.behinds})`;
  } else if (statToRender === "goalAccuracy") {
    if (rawStats.goals + rawStats.behinds === 0) {
      return "-";
    } else if (rawStats.behinds === 0) {
      return "1.000";
    } else {
      const accuracy = rawStats.goals / (rawStats.goals + rawStats.behinds);
      return accuracy.toFixed(3).slice(1);
    }
  } else if (statToRender === "disposalEfficiency") {
    if (rawStats.effectiveDisposals + rawStats.ineffectiveDisposals === 0) {
      return "-";
    } else if (rawStats.ineffectiveDisposals === 0) {
      return "1.000";
    } else {
      const efficiency =
        rawStats.effectiveDisposals /
        (rawStats.effectiveDisposals + rawStats.ineffectiveDisposals);
      return efficiency.toFixed(3).slice(1);
    }
  } else {
    // General case - direct access to the property
    return rawStats[statToRender];
  }
}

/**
 * Calculate delta between current and previous stat values
 * @param {Object} currentStats - Current player stats
 * @param {Object} previousStats - Previous player stats
 * @param {string} statKey - The stat key to compare
 * @returns {Object|null} - The delta info with value and isPositive flag, or null if no valid delta
 */
export function calculateStatDelta(currentStats, previousStats, statKey) {
  if (!currentStats || !previousStats) return null;

  // Handle special cases similar to renderStat function
  if (statKey === "score") {
    // For score, compare the total points but return formatted string
    const goalsDelta = currentStats.goals - previousStats.goals;
    const behindsDelta = currentStats.behinds - previousStats.behinds;
    const totalDelta =
      currentStats.goals * 6 +
      currentStats.behinds -
      (previousStats.goals * 6 + previousStats.behinds);

    if (totalDelta === 0) return null;

    // Format the delta in the same format as the score display
    const formattedDelta = `${Math.abs(goalsDelta)}-${Math.abs(behindsDelta)}`;
    return {
      value: formattedDelta,
      isPositive: totalDelta > 0,
    };
  } else if (statKey === "goalAccuracy" || statKey === "disposalEfficiency") {
    // For percentage-based stats, we skip delta calculation
    return null;
  } else {
    // General case - directly compare the stat values
    const currentValue = parseFloat(currentStats[statKey]);
    const previousValue = parseFloat(previousStats[statKey]);

    if (isNaN(currentValue) || isNaN(previousValue)) return null;

    const delta = currentValue - previousValue;
    if (delta === 0) return null;

    return {
      value: Math.abs(delta),
      isPositive: delta > 0,
    };
  }
}

/**
 * Renders the delta with appropriate styling
 * @param {Object|null} delta - The delta object with value and isPositive properties
 * @returns {JSX.Element|null} - React element for the delta or null
 */
export function renderStatDelta(delta) {
  if (delta === null) return null;

  const { value, isPositive } = delta;
  const prefix = isPositive ? "+" : "-";

  return (
    <div
      className={`text-xxxs font-semibold leading-tight ${isPositive ? "text-green-500 dark:text-green-400" : "text-red-500 dark:text-red-400"}`}
    >
      {prefix}
      {value}
    </div>
  );
}

/**
 * Calculate projected team scores and update DOM
 */
export function setTeamScoresToProjected() {
  const matchupStore = useMatchupStore.getState();

  const adjustedTeam1Stats = matchupStore.projTeam1Stats;
  const adjustedTeam2Stats = matchupStore.projTeam2Stats;

  const formula = matchupStore.formula;
  const statCols = formula.statCols;
  const weights = formula.values;

  let team1ProjScore = 0;
  let team2ProjScore = 0;

  statCols.forEach((stat) => {
    const weight = weights[stat];
    if (weight === 0) return; // Skip categories with no points

    const team1StatObj = { stats: adjustedTeam1Stats };
    const team2StatObj = { stats: adjustedTeam2Stats };

    // Determine which team wins this category
    const team1Wins = isTeamWinning(team1StatObj, team2StatObj, weights, stat);
    const team2Wins = isTeamWinning(team2StatObj, team1StatObj, weights, stat);

    // Award points to the winning team
    const categoryPoints = Math.abs(weight);
    if (team1Wins) {
      team1ProjScore += categoryPoints;
    } else if (team2Wins) {
      team2ProjScore += categoryPoints;
    }
  });

  // Broadcast the projected scores to the DOM
  const team1Element = document.querySelector("#team1ScoreFor");
  const team2Element = document.querySelector("#team2ScoreFor");

  if (team1Element) {
    team1Element.innerHTML = team1ProjScore.toString();
    team1Element.classList.add("text-gray-700", "italic", "dark:text-gray-200");
  }
  if (team2Element) {
    team2Element.innerHTML = team2ProjScore.toString();
    team2Element.classList.add("text-gray-700", "italic", "dark:text-gray-200");
  }
}

/**
 * Restore the original actual team scores to the DOM
 */
export function setTeamScoresToActual() {
  const matchupStore = useMatchupStore.getState();

  const actualTeam1Stats = matchupStore.actualTeam1Stats;
  const actualTeam2Stats = matchupStore.actualTeam2Stats;

  const team1Element = document.querySelector("#team1ScoreFor");
  const team2Element = document.querySelector("#team2ScoreFor");

  if (team1Element) {
    team1Element.innerHTML = actualTeam1Stats.scoreFor;
    team1Element.classList.remove(
      "text-gray-700",
      "italic",
      "dark:text-gray-200",
    );
  }
  if (team2Element) {
    team2Element.innerHTML = actualTeam2Stats.scoreFor;
    team2Element.classList.remove(
      "text-gray-700",
      "italic",
      "dark:text-gray-200",
    );
  }
}

/**
 * Calculate adjusted team stats that include projected stats for scoring players
 * @param {Object} teamStats - The team's base stats
 * @param {Array} projPlayerStats - Array of projected stats for all players
 * @param {Array} playerStats - Array of actual player stats
 * @param {Array} statCols - Array of stat columns to process
 * @returns {Object} - Adjusted team stats with projected values included
 */
export function calculateProjTeamStats(
  teamStats,
  projPlayerStats,
  playerStats,
  statCols,
) {
  if (!projPlayerStats || !teamStats) {
    return teamStats;
  }

  const projStatsByPlayerId = {};
  projPlayerStats.forEach((stats) => {
    projStatsByPlayerId[stats.player_id] = stats;
  });

  // Create lookup for players with actual stats
  const playerStatsByPlayerId = {};
  if (playerStats && playerStats.length > 0) {
    playerStats.forEach((stats) => {
      playerStatsByPlayerId[stats.player_id] = stats;
    });
  }

  // Start with a fresh stats object to build up from individual players
  const adjustedStats = {};

  // Initialize all stats to 0
  statCols.forEach((stat) => {
    if (stat === "score") {
      adjustedStats.goals = 0;
      adjustedStats.behinds = 0;
      adjustedStats.scoreFor = 0;
      adjustedStats.score = 0;
    } else if (stat === "disposalEfficiency") {
      adjustedStats.effectiveDisposals = 0;
      adjustedStats.ineffectiveDisposals = 0;
      adjustedStats.disposalEfficiency = 0;
    } else if (stat === "goalAccuracy") {
      adjustedStats.goalAccuracy = 0;
    } else {
      adjustedStats[stat] = 0;
    }
  });

  const scoringPlayerIds = teamStats.scoringPlayerIds || [];

  // Build team stats from blended player stats for all scoring players
  scoringPlayerIds.forEach((playerId) => {
    const projPlayerStats = projStatsByPlayerId[playerId];

    if (projPlayerStats) {
      // Add each player's blended stats to the team total
      statCols.forEach((stat) => {
        if (stat === "score") {
          // For score, we need to add goals and behinds separately
          const projGoals = projPlayerStats.goals || 0;
          const projBehinds = projPlayerStats.behinds || 0;

          adjustedStats.goals += projGoals;
          adjustedStats.behinds += projBehinds;

          // Update scoreFor and score which are the total points
          const totalPoints = adjustedStats.goals * 6 + adjustedStats.behinds;
          adjustedStats.scoreFor = totalPoints;
          adjustedStats.score = totalPoints;
        } else if (stat === "goalAccuracy") {
          // Skip goalAccuracy - it'll be computed from goals and behinds
        } else if (stat === "disposalEfficiency") {
          // For disposalEfficiency, we need to ensure the component stats are added
          const projEffective = projPlayerStats.effectiveDisposals || 0;
          const projIneffective = projPlayerStats.ineffectiveDisposals || 0;

          adjustedStats.effectiveDisposals += projEffective;
          adjustedStats.ineffectiveDisposals += projIneffective;
        } else {
          const projValue = projPlayerStats[stat] || 0;
          adjustedStats[stat] += projValue;
        }
      });
    }
  });

  // Recalculate efficiency stats after adding projected values
  if (
    adjustedStats.goals !== undefined &&
    adjustedStats.behinds !== undefined
  ) {
    if (adjustedStats.goals + adjustedStats.behinds === 0) {
      adjustedStats.goalAccuracy = 0;
    } else {
      adjustedStats.goalAccuracy =
        adjustedStats.goals / (adjustedStats.goals + adjustedStats.behinds);
    }
  }

  if (
    adjustedStats.effectiveDisposals !== undefined &&
    adjustedStats.ineffectiveDisposals !== undefined
  ) {
    if (
      adjustedStats.effectiveDisposals + adjustedStats.ineffectiveDisposals ===
      0
    ) {
      adjustedStats.disposalEfficiency = 0;
    } else {
      adjustedStats.disposalEfficiency =
        adjustedStats.effectiveDisposals /
        (adjustedStats.effectiveDisposals + adjustedStats.ineffectiveDisposals);
    }
  }

  return adjustedStats;
}

/**
 * Calculate and store projected team stats in the store using blended stats
 * @param {Object} staticData - The static data containing teams and projected stats
 */
export function calculateAndStoreProjTeamStats(staticData) {
  const matchupStore = useMatchupStore.getState();
  const { playerStats } = matchupStore;

  // We need to get the statCols from the formula
  const statCols = staticData?.formula?.statCols;

  if (!statCols) {
    // If no formula, just use the original stats
    matchupStore.setProjTeam1Stats(staticData?.team1?.stats);
    matchupStore.setProjTeam2Stats(staticData?.team2?.stats);
    return;
  }

  // Wait for blended stats to be calculated first, then use them for team calculations
  // This function will be called again after blended stats are ready
  const blendedStats = matchupStore.projStats;

  if (!blendedStats) {
    // If no blended stats available yet, use original stats as fallback
    matchupStore.setProjTeam1Stats(staticData?.team1?.stats);
    matchupStore.setProjTeam2Stats(staticData?.team2?.stats);
    return;
  }

  // Calculate projected team stats using blended player stats
  const projectedTeam1Stats = calculateProjTeamStats(
    staticData?.team1?.stats,
    blendedStats,
    playerStats,
    statCols,
  );

  const projectedTeam2Stats = calculateProjTeamStats(
    staticData?.team2?.stats,
    blendedStats,
    playerStats,
    statCols,
  );

  matchupStore.setProjTeam1Stats(projectedTeam1Stats);
  matchupStore.setProjTeam2Stats(projectedTeam2Stats);
}

/**
 * Calculate blended projected stats based on game completion percentage
 * Formula: blended_stats = actual_stats + projected_stats * (1 - pct_complete)
 * @param {Object} staticData - The static data containing teams and projected stats
 */
export function calculateAndStoreProjPlayerStats(staticData) {
  const matchupStore = useMatchupStore.getState();
  const { playerStats, sportFixtures } = matchupStore;

  if (!staticData?.projStats || !staticData?.formula?.statCols) {
    // If no projected stats or formula, just use the original projected stats
    matchupStore.setProjStats(staticData?.projStats);
    return;
  }

  // Helper function to get game completion percentage for a player
  const getGameCompletionPercentage = (playerId, playerData) => {
    if (!playerData?.teamId || !sportFixtures) {
      return 0;
    }

    const fixture = sportFixtures.find((f) =>
      f.teams?.some((teamObj) => teamObj.team?.id === playerData.teamId),
    );

    if (!fixture) {
      return 0;
    }

    // Use pctComplete from fixture, defaulting to 0 if not available
    return fixture.pctComplete || 0;
  };

  // Helper function to get player data from lineups
  const getPlayerData = (playerId) => {
    const team1Players = staticData.team1?.lineup?.players || [];
    const team2Players = staticData.team2?.lineup?.players || [];

    return [...team1Players, ...team2Players].find((p) => p.id === playerId);
  };

  // Create lookup for actual player stats
  const actualStatsByPlayerId = {};
  if (playerStats && playerStats.length > 0) {
    playerStats.forEach((stats) => {
      actualStatsByPlayerId[stats.player_id] = stats;
    });
  }

  // Calculate blended stats for each player
  const blendedStats = staticData.projStats.map((projPlayerStats) => {
    const playerId = projPlayerStats.player_id;
    const actualStats = actualStatsByPlayerId[playerId];
    const playerData = getPlayerData(playerId);

    // If no actual stats exist, use full projected stats
    if (!actualStats) {
      return { ...projPlayerStats };
    }

    // Get game completion percentage
    const pctComplete = getGameCompletionPercentage(playerId, playerData);
    const projectionWeight = Math.max(0, Math.min(1, 1 - pctComplete));

    // Calculate blended stats for each stat column
    const blendedPlayerStats = { ...actualStats };

    staticData.formula.statCols.forEach((statKey) => {
      if (statKey === "score") {
        // Handle score components separately
        const actualGoals = actualStats.goals || 0;
        const actualBehinds = actualStats.behinds || 0;
        const projGoals = projPlayerStats.goals || 0;
        const projBehinds = projPlayerStats.behinds || 0;

        const blendedGoals = Math.round(
          actualGoals + projGoals * projectionWeight,
        );
        const blendedBehinds = Math.round(
          actualBehinds + projBehinds * projectionWeight,
        );

        blendedPlayerStats.goals = blendedGoals;
        blendedPlayerStats.behinds = blendedBehinds;
      } else if (
        statKey === "goalAccuracy" ||
        statKey === "disposalEfficiency"
      ) {
        // Skip calculated fields - they'll be computed from component stats
      } else {
        const actualValue = actualStats[statKey] || 0;
        const projValue = projPlayerStats[statKey] || 0;
        const blendedValue = Math.round(
          actualValue + projValue * projectionWeight,
        );

        blendedPlayerStats[statKey] = blendedValue;
      }
    });

    return blendedPlayerStats;
  });

  // Store the blended stats in the store
  matchupStore.setProjStats(blendedStats);
}

export function setupTooltips(containerRef, dependencies = []) {
  useEffect(() => {
    // Set up tooltips when component mounts or updates
    if (containerRef.current) {
      // Destroy any existing tooltips to prevent duplicates
      const tooltipNodes = containerRef.current.querySelectorAll(
        "[data-tippy-content]",
      );
      tooltipNodes.forEach((node) => {
        if (node._tippy) {
          node._tippy.destroy();
        }
      });

      // Initialize tooltips on elements with data-tippy-content
      tippy("[data-tippy-content]", {
        placement: "top",
        arrow: true,
        animation: "shift-away-subtle",
        interactive: false,
        allowHTML: true,
        delay: [50, 0],
        theme: "custom",
        onShow(instance) {
          instance.popper
            .querySelector(".tippy-box")
            .classList.add(
              "bg-gray-800",
              "text-white",
              "dark:bg-gray-700",
              "text-sm",
            );
          instance.popper
            .querySelector(".tippy-arrow")
            .classList.add("text-gray-800", "dark:text-gray-700");
        },
      });
    }
  }, dependencies); // Re-run when dependencies change
}
